/*******************************************************
* Toast Context
* 
* Provides a global toast notification system for displaying alerts and messages
* throughout the application.
* 
* Key Features:
* - Manages a queue of toast notifications
* - Supports different severity levels (success, error, warning, info)
* - Customizable duration for each toast
* - Auto-dismissal and manual dismissal options
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import React, { createContext, useContext, useCallback, useState } from 'react';
import { Snackbar, Alert, type AlertColor } from '@mui/material';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
export interface Toast {
    id: string;
    message: string;
    severity: AlertColor;
    duration?: number;
}

interface ToastContextType {
    addToast: (message: string, severity: AlertColor, duration?: number) => void;
    removeToast: (id: string) => void;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const ToastContext = createContext<ToastContextType | undefined>(undefined);

//#region CONSTANT - STYLES
const TOAST_CONTAINER_STYLE = {
    position: 'fixed' as const,
    bottom: 40,
    right: 16,
    zIndex: 2000
};

const TOAST_STYLE = {
    position: 'relative' as const,
    marginTop: '8px' // rails-ignore: a plain style prop, so no theme units
};
//#endregion
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
//#endregion


export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((message: string, severity: AlertColor, duration = 3000) => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts((prev) => [...prev, { id, message, severity, duration }]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);
    //#endregion


    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <ToastContext.Provider value={{ addToast, removeToast }}>
            {children}
            <div style={TOAST_CONTAINER_STYLE}>
                {toasts.map((toast) => (
                    <Snackbar
                        key={toast.id}
                        open={true}
                        autoHideDuration={toast.duration}
                        onClose={() => removeToast(toast.id)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        style={TOAST_STYLE}
                    >
                        <Alert
                            onClose={() => removeToast(toast.id)}
                            severity={toast.severity}

                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {toast.message}
                        </Alert>
                    </Snackbar>
                ))}
            </div>
        </ToastContext.Provider>
    );
    //#endregion
}; 